import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './LoginForm.css';
import { LoginRequest } from '../services/authService';

// Use the same interface as the API service
type LoginData = LoginRequest;

interface LoginFormProps {
  onLogin?: (loginData: LoginData) => Promise<void>;
  redirectUrl?: string | null;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLogin, redirectUrl }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginData>({
    companyName: '',
    userId: '',
    userPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<LoginData>>({});

  const validateField = (field: keyof LoginData, value: string): string | null => {
    switch (field) {
      case 'companyName':
        if (!value.trim()) {
          return 'インスタンス名を入力してください。';
        }
        if (value.length < 2) {
          return '2文字以上の文字列である必要があります。';
        }
        if (value.length > 255) {
          return '255文字以下で入力してください。';
        }
        break;
      case 'userId':
        if (!value.trim()) {
          return 'ユーザーIDを入力してください。';
        }
        if (value.length < 2) {
          return '2文字以上の文字列である必要があります。';
        }
        if (value.length > 100) {
          return '100文字以下で入力してください。';
        }
        // if (!/^[a-zA-Z0-9_]+$/.test(value)) {
        //   return 'ユーザーIDは英数字とアンダースコアのみ使用できます';
        // }
        break;
      case 'userPassword':
        if (!value.trim()) {
          return 'パスワードを入力してください。';
        }
        if (value.length < 2) {
          return '2文字以上の文字列である必要があります。';
        }
        if (value.length > 100) {
          return '100文字以下で入力してください。';
        }
        break;
    }
    return null;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginData> = {};
    let isValid = true;

    // Validate each field
    Object.keys(formData).forEach((key) => {
      const field = key as keyof LoginData;
      const error = validateField(field, formData[field].trim());
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Check if form is complete and valid for enabling submit button
  const isFormComplete = (): boolean => {
    return formData.companyName.trim().length >= 2 && formData.companyName.trim().length <= 255 &&
           formData.userId.trim().length >= 2 && formData.userId.trim().length <= 100 &&
           formData.userPassword.trim().length >= 2 && formData.userPassword.trim().length <= 100;
  };

  const handleInputChange = (field: keyof LoginData, value: string) => {
    // Limit input length based on field
    let maxLength = 255; // default
    switch (field) {
      case 'companyName':
        maxLength = 255;
        break;
      case 'userId':
      case 'userPassword':
        maxLength = 100;
        break;
    }

    // Truncate if exceeds max length
    if (value.length > maxLength) {
      value = value.substring(0, maxLength);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleBlur = (field: keyof LoginData) => {
    // Validate field on blur
    const error = validateField(field, formData[field]);
    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      if (onLogin) {
        await onLogin(formData);
        toast.success('ログインに成功しました');
        
        // Redirect to the intended page after successful login
        if (redirectUrl) {
          navigate(redirectUrl, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      // API errors are handled by authService and shown via toast
      // No need to set inline errors here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img 
            src="/images/login-logo.png" 
            alt="コラボフロー for 商奉行 ロゴ" 
            className="login-logo"
          />
          <h2 className="login-app-title">コラボフロー for 商奉行</h2>
          <div className="login-divider"></div>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-field">
            <label htmlFor="companyName" className="login-label">
              インスタンス名
            </label>
            <input
              type="text"
              id="companyName"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              onBlur={() => handleBlur('companyName')}
              className={`login-input ${errors.companyName ? 'error' : ''}`}
              placeholder="インスタンス名を入力"
              maxLength={255}
              disabled={isLoading}
            />
            {errors.companyName && (
              <span className="login-field-error">{errors.companyName}</span>
            )}
          </div>

          <div className="login-field">
            <label htmlFor="userId" className="login-label">
              ユーザーID
            </label>
            <input
              type="text"
              id="userId"
              value={formData.userId}
              onChange={(e) => handleInputChange('userId', e.target.value)}
              onBlur={() => handleBlur('userId')}
              className={`login-input ${errors.userId ? 'error' : ''}`}
              placeholder="ユーザーIDを入力"
              maxLength={100}
              disabled={isLoading}
            />
            {errors.userId && (
              <span className="login-field-error">{errors.userId}</span>
            )}
          </div>

          <div className="login-field">
            <label htmlFor="userPassword" className="login-label">
              パスワード
            </label>
            <input
              type="password"
              id="userPassword"
              value={formData.userPassword}
              onChange={(e) => handleInputChange('userPassword', e.target.value)}
              onBlur={() => handleBlur('userPassword')}
              className={`login-input ${errors.userPassword ? 'error' : ''}`}
              placeholder="パスワードを入力"
              maxLength={100}
              disabled={isLoading}
            />
            {errors.userPassword && (
              <span className="login-field-error">{errors.userPassword}</span>
            )}
          </div>

          <button
            type="submit"
            className={`login-button ${!isFormComplete() ? 'disabled' : ''}`}
            disabled={isLoading || !isFormComplete()}
          >
            {isLoading ? 'ログイン中...' : 'ログイン'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
